import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Rocket } from "lucide-react";
import { getOnboardingProgram, clientCompleteMilestone, type Milestone } from "../../api/programs";
import useRetryFetch from "../../hooks/useRetryFetch";
import useAuth from "../../hooks/useAuth";
import { cn } from "../../lib/utils";
import { Skeleton } from "../ui/skeleton";
import MilestoneRow from "../programs/MilestoneRow";

const OnboardingProgramSection: React.FC = () => {
  const { auth } = useAuth();
  const retryFetch = useRetryFetch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expandedMilestoneId, setExpandedMilestoneId] = useState<string | null>(null);
  const [completing, setCompleting] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["programs", "onboarding"],
    queryFn: () => getOnboardingProgram(retryFetch),
    enabled: !!auth?.accessToken,
    staleTime: 1000 * 60 * 5,
  });

  const program = data?.program;

  if (!auth?.accessToken) return null;

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-6 w-48 rounded-lg" />
        <Skeleton className="h-20 rounded-2xl" />
        <Skeleton className="h-16 rounded-2xl" />
        <Skeleton className="h-16 rounded-2xl" />
      </div>
    );
  }

  if (!program || program.status === "completed") return null;

  const sortedMilestones = [...program.milestones].sort((a, b) => a.order - b.order);
  const completedCount = program.milestones.filter((m) => m.status === "completed").length;
  const totalCount = program.milestones.length;
  const pct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const handleComplete = async (milestoneId: string) => {
    if (completing) return;
    setCompleting(milestoneId);
    try {
      await clientCompleteMilestone(retryFetch, program._id, milestoneId);
      await queryClient.invalidateQueries({ queryKey: ["programs", "onboarding"] });
    } catch {
      // silently fail — user can retry
    } finally {
      setCompleting(null);
    }
  };

  const handleOpen = (milestone: Milestone) => {
    if (milestone.type === "activity") {
      if (!milestone.linkedEntityId) return;
      navigate(`/activities/run?wid=${milestone.linkedEntityId}`);
    } else if (milestone.type === "meeting") {
      if (!milestone.linkedEntityId) return;
      navigate(`/meeting/join/${milestone.linkedEntityId}`);
    } else if (milestone.type === "identity_space") {
      navigate("/spaces", milestone.linkedEntityId ? { state: { selectedSpaceId: milestone.linkedEntityId } } : undefined);
    } else {
      // action_item — open workspace
      navigate(`/portal?tab=my_workspace`);
    }
  };

  const handleExpand = (milestoneId: string) => {
    setExpandedMilestoneId((prev) => (prev === milestoneId ? null : milestoneId));
  };

  return (
    <div className="rounded-2xl border border-primary/20 bg-primary/5 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-4 pb-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
            <Rocket className="w-4 h-4 text-primary" />
          </div>
          <div>
            <p className="text-xs font-medium text-primary/70 uppercase tracking-wide leading-none mb-0.5">
              Getting Started
            </p>
            <p className="text-sm font-semibold text-foreground">{program.title}</p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/programs/${program._id}`)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          View all
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="px-4 pb-3">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-muted-foreground">
            {completedCount} of {totalCount} completed
          </span>
          <span className={cn("text-xs font-medium", pct === 100 ? "text-primary" : "text-emerald-600")}>
            {pct}%
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-primary/10 overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Milestones */}
      <div className={cn("px-3 pb-3 space-y-2")}>
        {sortedMilestones.map((milestone) => (
          <MilestoneRow
            key={milestone.id}
            milestone={milestone}
            programId={program._id}
            readonly
            onComplete={completing ? undefined : handleComplete}
            onOpen={handleOpen}
            isExpanded={expandedMilestoneId === milestone.id}
            onExpand={handleExpand}
          />
        ))}
      </div>
    </div>
  );
};

export default OnboardingProgramSection;
